import React, { useState, useEffect, useRef } from 'react';
import { useShift } from '../context/ShiftContext';
import { extractErrorMessage } from '../utils/extractError';

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

interface CompletedSummary {
  duration_minutes: number;
  alerts_sent: number;
  activities_logged: number;
}

export default function ShiftStatusBar() {
  const { activeShift, loading, clockIn, clockOut } = useShift();
  const [clockingIn, setClockingIn] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [handoffNotes, setHandoffNotes] = useState('');
  const [clockingOut, setClockingOut] = useState(false);
  const [error, setError] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [summary, setSummary] = useState<CompletedSummary | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Live timer
  useEffect(() => {
    if (activeShift) {
      const clockInTime = new Date(activeShift.clock_in).getTime();
      const tick = () => {
        setElapsed(Math.floor((Date.now() - clockInTime) / 1000));
      };
      tick();
      timerRef.current = setInterval(tick, 1000);
      return () => {
        if (timerRef.current) clearInterval(timerRef.current);
      };
    } else {
      setElapsed(0);
    }
  }, [activeShift]);

  // Auto-dismiss summary
  useEffect(() => {
    if (summary) {
      const t = setTimeout(() => setSummary(null), 4000);
      return () => clearTimeout(t);
    }
  }, [summary]);

  const handleClockIn = async () => {
    setError('');
    setClockingIn(true);
    try {
      await clockIn();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to clock in'));
    } finally {
      setClockingIn(false);
    }
  };

  const handleClockOut = async () => {
    setError('');
    setClockingOut(true);
    try {
      const result = await clockOut(handoffNotes);
      setSummary({
        duration_minutes: result.duration_minutes,
        alerts_sent: result.alerts_sent,
        activities_logged: result.activities_logged,
      });
      setShowModal(false);
      setHandoffNotes('');
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to clock out'));
    } finally {
      setClockingOut(false);
    }
  };

  if (loading) return null;

  // Summary banner after clock-out
  if (summary) {
    return (
      <div style={styles.bar}>
        <div style={styles.barInner}>
          <span style={styles.summaryText}>
            Shift ended — {summary.duration_minutes} min · {summary.alerts_sent} alerts · {summary.activities_logged} activities
          </span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={styles.bar}>
        <div style={styles.barInner}>
          {!activeShift ? (
            // Clocked out state
            <div style={styles.row}>
              <span style={styles.offDutyDot} />
              <span style={styles.statusLabel}>Off Shift</span>
              <button
                onClick={handleClockIn}
                disabled={clockingIn}
                style={styles.clockInBtn}
              >
                {clockingIn ? 'Clocking In...' : 'Clock In'}
              </button>
              {error && <span style={styles.errorText}>{error}</span>}
            </div>
          ) : (
            // Clocked in state
            <div style={styles.row}>
              <span style={styles.liveDot} />
              <span style={styles.onShiftLabel}>On Shift</span>
              <span style={styles.timer}>{formatDuration(elapsed)}</span>
              <span style={styles.statsText}>
                {activeShift.alerts_sent} alerts · {activeShift.activities_logged} activities
              </span>
              <button
                onClick={() => { setShowModal(true); setError(''); }}
                style={styles.clockOutBtn}
              >
                Clock Out
              </button>
              {error && <span style={styles.errorText}>{error}</span>}
            </div>
          )}
        </div>
      </div>

      {/* Clock-Out Modal */}
      {showModal && (
        <div style={styles.overlay} onClick={() => { if (!clockingOut) setShowModal(false); }}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>End Shift</h3>
            <p style={styles.modalSubtext}>
              You've been on shift for <strong style={{ color: 'var(--text-primary)' }}>{formatDuration(elapsed)}</strong>
            </p>
            <label style={styles.label}>Handoff Notes (optional)</label>
            <textarea
              value={handoffNotes}
              onChange={(e) => setHandoffNotes(e.target.value)}
              style={styles.textarea}
              placeholder="Notes for the next monitor..."
              rows={4}
            />
            {error && <div style={styles.modalError}>{error}</div>}
            <div style={styles.modalActions}>
              <button
                onClick={() => { setShowModal(false); setError(''); }}
                style={styles.cancelBtn}
                disabled={clockingOut}
              >
                Cancel
              </button>
              <button
                onClick={handleClockOut}
                disabled={clockingOut}
                style={styles.endShiftBtn}
              >
                {clockingOut ? 'Ending Shift...' : 'End Shift'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const styles: Record<string, React.CSSProperties> = {
  bar: {
    background: 'var(--bg-surface)',
    borderBottom: '1px solid var(--border-subtle)',
  },
  barInner: {
    maxWidth: 960,
    margin: '0 auto',
    padding: '6px 20px',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
  },
  offDutyDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: 'var(--text-tertiary)',
    flexShrink: 0,
  },
  liveDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: 'var(--status-success)',
    boxShadow: '0 0 6px var(--status-success)',
    flexShrink: 0,
  },
  statusLabel: {
    fontFamily: 'var(--font-body)',
    fontSize: 'var(--text-sm)',
    color: 'var(--text-tertiary)',
    fontWeight: 500,
  },
  onShiftLabel: {
    fontFamily: 'var(--font-body)',
    fontSize: 'var(--text-sm)',
    color: 'var(--status-success)',
    fontWeight: 600,
  },
  timer: {
    fontFamily: 'var(--font-mono)',
    fontSize: 'var(--text-sm)',
    color: 'var(--text-primary)',
    fontWeight: 500,
    letterSpacing: '0.5px',
  },
  statsText: {
    fontFamily: 'var(--font-body)',
    fontSize: 'var(--text-xs)',
    color: 'var(--text-tertiary)',
    marginLeft: 2,
  },
  clockInBtn: {
    marginLeft: 'auto',
    padding: '4px 14px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--status-success)',
    background: 'var(--status-success-muted)',
    color: 'var(--status-success)',
    fontFamily: 'var(--font-body)',
    fontSize: 'var(--text-xs)',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  clockOutBtn: {
    marginLeft: 'auto',
    padding: '4px 14px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--status-critical)',
    background: 'var(--status-critical-muted)',
    color: 'var(--status-critical)',
    fontFamily: 'var(--font-body)',
    fontSize: 'var(--text-xs)',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  errorText: {
    fontFamily: 'var(--font-body)',
    fontSize: 'var(--text-xs)',
    color: 'var(--status-critical)',
  },
  summaryText: {
    fontFamily: 'var(--font-body)',
    fontSize: 'var(--text-sm)',
    color: 'var(--accent-text)',
    fontWeight: 500,
  },
  // Modal
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(10, 10, 12, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: 'var(--bg-surface-raised)',
    borderRadius: 'var(--radius-xl)',
    border: '1px solid var(--border-default)',
    padding: '28px 32px',
    width: '100%',
    maxWidth: 440,
    boxShadow: 'var(--shadow-lg)',
  },
  modalTitle: {
    fontFamily: 'var(--font-display)',
    fontSize: 'var(--text-xl)',
    fontWeight: 600,
    color: 'var(--text-primary)',
    margin: '0 0 8px',
  },
  modalSubtext: {
    fontFamily: 'var(--font-body)',
    fontSize: 'var(--text-md)',
    color: 'var(--text-secondary)',
    margin: '0 0 20px',
  },
  label: {
    fontFamily: 'var(--font-body)',
    fontSize: 'var(--text-sm)',
    fontWeight: 500,
    color: 'var(--text-secondary)',
    marginBottom: 6,
    display: 'block',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border-default)',
    background: 'var(--input-bg)',
    color: 'var(--text-primary)',
    fontFamily: 'var(--font-body)',
    fontSize: 'var(--text-md)',
    resize: 'vertical' as const,
    boxSizing: 'border-box' as const,
  },
  modalError: {
    background: 'var(--status-critical-muted)',
    color: 'var(--status-critical)',
    padding: '10px 14px',
    borderRadius: 'var(--radius-md)',
    fontFamily: 'var(--font-body)',
    fontSize: 'var(--text-sm)',
    marginTop: 12,
    border: '1px solid rgba(255, 71, 87, 0.2)',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 20,
  },
  cancelBtn: {
    padding: '8px 20px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-default)',
    background: 'transparent',
    fontFamily: 'var(--font-body)',
    fontSize: 'var(--text-sm)',
    fontWeight: 500,
    color: 'var(--text-secondary)',
    cursor: 'pointer',
  },
  endShiftBtn: {
    padding: '8px 20px',
    borderRadius: 'var(--radius-md)',
    border: 'none',
    background: 'var(--status-critical)',
    color: '#fff',
    fontFamily: 'var(--font-body)',
    fontSize: 'var(--text-sm)',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
};
